import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, UtensilsCrossed } from 'lucide-react';
import { useToast } from '../hooks/useToast.tsx';
import { useRestaurantName } from '../hooks/useRestaurantName';
import { APP_VERSION } from '../version';

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { success, error } = useToast();
  const restaurantName = useRestaurantName();
  const [nome, setNome] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    try {
      await login(nome, password, rememberMe);
      success('Login realizado com sucesso');
      navigate('/mapa');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro no login';
      setErrorMsg(message);
      error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-brand">
            <div className="login-logo" aria-hidden="true">
              <UtensilsCrossed className="w-7 h-7" />
            </div>
            <div className="login-header">
              <h1>{restaurantName}</h1>
              <p>Sistema de gestão de reservas</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {errorMsg && (
              <div className="form-error" role="alert">
                {errorMsg}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="nome" className="label">Utilizador</label>
              <input
                type="text"
                id="nome"
                className="input"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome de utilizador"
                autoComplete="username"
                required
                disabled={loading}
                aria-describedby={errorMsg ? 'login-error' : undefined}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="label">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar password' : 'Mostrar password'}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="form-group form-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span>Lembrar-me</span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  <span>A entrar...</span>
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>v{APP_VERSION}</p>
          </div>
        </div>
      </div>
    </div>
  );
}